import { ASSISTANT_EXAMPLES, type AssistantReply } from '@shared/assistant';
import type { FilterGroup, SortSpec } from '@shared/filters';
import { useMutation } from '@tanstack/react-query';
import { CornerDownLeft, Sparkles, Wand2 } from 'lucide-react';
import { useState } from 'react';
import { Button } from '../../components/ui/button';
import { Alert, Badge } from '../../components/ui/feedback';
import { Input } from '../../components/ui/form';
import { api } from '../../lib/api';
import { errorMessage } from '../../lib/session';

export interface AssistantPanelProps {
  projectId: string | undefined;
  onApply: (filter: FilterGroup, sort: SortSpec[] | undefined) => void;
}

/**
 * Ask for a subset of the results in plain language.
 *
 * The assistant only ever produces a *filter*, which is then run against the database — so the
 * count shown is the real count, and the filter it built is visible and editable rather than
 * being an answer you would have to take on trust.
 */
export function AssistantPanel({ projectId, onApply }: AssistantPanelProps) {
  const [question, setQuestion] = useState('');
  const [reply, setReply] = useState<AssistantReply | null>(null);

  const ask = useMutation({
    mutationFn: (text: string) =>
      api.post<AssistantReply>('/api/assistant/ask', {
        ...(projectId ? { projectId } : {}),
        question: text,
      }),
    onSuccess: (result) => {
      setReply(result);
      // A filter is only useful once it is on the table, so apply it as soon as it arrives.
      if (result.action) onApply(result.action.filter, result.action.sort);
    },
  });

  const submit = (text: string): void => {
    const trimmed = text.trim();
    if (trimmed.length < 3) return;
    setQuestion(trimmed);
    ask.mutate(trimmed);
  };

  return (
    <div className="space-y-2">
      <div className="flex flex-wrap items-center gap-2">
        <span className="flex items-center gap-1 text-[13px] text-muted-foreground">
          <Wand2 className="h-3.5 w-3.5" aria-hidden="true" />
          Ask
        </span>
        <div className="relative min-w-64 flex-1">
          <Input
            className="pr-8"
            value={question}
            placeholder="with an email but no website"
            aria-label="Describe the businesses you want to see"
            onChange={(event) => setQuestion(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === 'Enter') submit(question);
            }}
          />
          <CornerDownLeft
            className="pointer-events-none absolute right-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground"
            aria-hidden="true"
          />
        </div>
        <Button size="sm" loading={ask.isPending} disabled={question.trim().length < 3} onClick={() => submit(question)}>
          Filter
        </Button>
      </div>

      {!reply && !ask.isPending ? (
        <div className="flex flex-wrap items-center gap-1.5">
          <span className="text-[12px] text-muted-foreground">For example:</span>
          {ASSISTANT_EXAMPLES.map((example) => (
            <button
              key={example}
              type="button"
              className="rounded-full border border-border px-2.5 py-0.5 text-[12px] text-muted-foreground transition-colors duration-150 hover:border-accent hover:text-accent"
              onClick={() => submit(example)}
            >
              {example}
            </button>
          ))}
        </div>
      ) : null}

      {ask.error ? <Alert tone="error">{errorMessage(ask.error)}</Alert> : null}

      {reply ? (
        <Alert tone={reply.action ? 'success' : 'warning'}>
          <span className="flex flex-wrap items-center gap-2">
            <span>{reply.answer}</span>
            <Badge tone="neutral">
              <Sparkles className="mr-0.5 h-3 w-3" aria-hidden="true" />
              {reply.provider === 'gemini' ? 'AI-built filter' : 'Built from rules'}
            </Badge>
          </span>
        </Alert>
      ) : null}
    </div>
  );
}
